import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromJS, Map } from 'immutable';
import { CustomValidators } from 'ngx-custom-validators';
import {
  IAddManualPayment,
  IAddPaymentPayload,
  ISendReceiptPreview,
} from '../../../payments-data/payments-data.data';
import { Document } from '../../../document-data/document.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { IFormMasterBuilder } from '../../../form-master/form-master.interface';
import { IRendererOptions } from '../../../renderer/renderer.interface';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { IAutoCompleteOptions } from '../../../shared/directives/auto-complete/auto-complete.interface';
import { DateTimeService } from 'i2g-ng-utils';
import { manualPaymentMethodTmpl } from './builder-component-tmpls/auto-complete.tmpl';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Injectable()
export class ManualPaymentDialogBuilder implements IFormMasterBuilder {
  FORM_KEYS = {
    FULLY_PAID: 'fullyPaid',
    AMOUNT: 'amount',
    METHOD: 'method',
    DATE: 'date',
    NOTE: 'note',
    SEND_RECEIPT: 'sendReceipt',
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _dateTimeService: DateTimeService,
    private _translateService: TranslateService,
    private _domSanitizer: DomSanitizer
  ) {}

  buildForm(
    calculationResult: CalculationResult,
    asFullyPaid: boolean,
    sendReceiptEnabled: boolean
  ): FormGroup {
    return this._formBuilder.group({
      [this.FORM_KEYS.FULLY_PAID]: [asFullyPaid],
      [this.FORM_KEYS.AMOUNT]: [
        asFullyPaid ? calculationResult.outstandingBalance : 0,
        [
          CustomValidators.number,
          CustomValidators.range([-9999999999999, 9999999999999]),
        ],
      ],
      [this.FORM_KEYS.METHOD]: ['', Validators.maxLength(100)],
      [this.FORM_KEYS.DATE]: [this._dateTimeService.getDateString()],
      [this.FORM_KEYS.NOTE]: ['', Validators.maxLength(1000)],
      [this.FORM_KEYS.SEND_RECEIPT]: [sendReceiptEnabled],
    });
  }

  buildSendReceiptPreview(
    form: Map<string, any>,
    dialogType: string,
    renderingOptions: IRendererOptions,
    document: Document
  ): ISendReceiptPreview {
    return {
      transaction: this.buildTransactionPayload(form),
      document,
      dialogType,
      renderingOptions,
    };
  }

  buildTransactionPayload(form: Map<string, any>): IAddPaymentPayload {
    return {
      amount: form.get(this.FORM_KEYS.AMOUNT) || 0,
      method_detail: form.get(this.FORM_KEYS.METHOD) || null,
      description: form.get(this.FORM_KEYS.NOTE) || null,
      datetime: this._dateTimeService.getMidDayUtcOfDate(
        form.get(this.FORM_KEYS.DATE)
      ),
    };
  }

  buildAddManualPayment(
    form: Map<string, any>,
    renderingOptions: IRendererOptions,
    document: Document
  ): IAddManualPayment {
    return {
      addPayment: this.buildTransactionPayload(form),
      sendReceipt: form.get(this.FORM_KEYS.SEND_RECEIPT),
      renderingOptions,
      document,
    };
  }

  buildRenderingOptions(
    paymentsSettings: PaymentsSettings,
    document: Document,
    bankTransferPermission: boolean,
    cardPaymentsPermission: boolean,
    paypalPermission: boolean
  ): IRendererOptions {
    return {
      disable_pay_now_link: false,
      card_payments_globally_enabled:
        cardPaymentsPermission && paymentsSettings.cardPaymentsEnabled(),
      paypal_ec_globally_enabled:
        paypalPermission && paymentsSettings.paypalEcEnabled,
      bank_transfer_globally_enabled:
        bankTransferPermission && paymentsSettings.bankTransferEnabled(),
    };
  }

  buildMethodOptions(): { autoComplete: IAutoCompleteOptions } {
    const methodSearchKey = 'methodValue';
    const defaultMethods = [
      this._translateService.instant('PAYMENT_METHOD.CASH'),
      this._translateService.instant('PAYMENT_METHOD.CHECK'),
      this._translateService.instant('PAYMENT_METHOD.BANK_TRANSFER'),
      this._translateService.instant('PAYMENT_METHOD.CREDIT_CARD'),
      this._translateService.instant('PAYMENT_METHOD.PAYPAL'),
    ];

    return {
      autoComplete: {
        data: defaultMethods.map((method) =>
          fromJS({ [methodSearchKey]: method })
        ),
        searchableKeys: [methodSearchKey],
        blankSearch: true,
        itemTemplate: manualPaymentMethodTmpl.bind(null, methodSearchKey),
        setFn: (method: Map<string, any>) => method.get(methodSearchKey),
        emitEventOnSet: true,
        highlighter: true,
        highlightCompleteWords: true,
      },
    };
  }

  buildTerminalUrl(paymentUrl: string, isKicEnablingPayment: boolean): SafeUrl {
    if (!paymentUrl) {
      return null;
    }

    const suffix = isKicEnablingPayment ? '?show_payment_enablement=true' : '';

    return this._domSanitizer.bypassSecurityTrustResourceUrl(
      `${paymentUrl}/terminal${suffix}`
    );
  }
}
